import React from 'react';

import { ThemeProvider } from '@mui/material/styles';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import * as loginPageConst from '../../const/loginPageConst';
import { StyledTextField } from '../styled/loginPageStyled';
import theme from '../../../theme';

type FromListType = {
	id: string;
	label: string;
	defaultValue?: string;
	type?: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}[];

type LoginPageDialogType = {
	id: string;
	open: boolean;
	dialogTitle: string;
	dialogText: string;
	dialogSupplementaryText?: string;
	companyCode?: string;
	userCode?: string;
	fromList: FromListType;
	nextButtonName: string;
	onCloseDialog: () => void;
	onClickBack?: () => void;
	onClickNext: () => Promise<void>;
	isError: boolean;
	isLoginLockError?: boolean;
	needsChangeInitPassword?: boolean;
	isLocalStorageError: boolean;
	onCloseErrorAlert: () => void;
	onCloseLoginLockErrorAlert?: () => void;
	onCloseLocalStorageErrorAlert: () => void;
	onCloseUnauthorizedErrorAlert?: () => void;
	onClickRedirect?: () => void;
};

export const LoginPageDialog = (input: LoginPageDialogType) => {
	return (
		<ThemeProvider theme={theme}>
			<Dialog id={input.id} open={input.open} onClose={input.onCloseDialog} maxWidth="sm">
				{/* ダイアログのタイトル */}
				<DialogTitle>
					<Typography sx={{ fontSize: 17, fontWeight: 'bold', color: theme.palette.primary.dark }}>{input.dialogTitle}</Typography>
				</DialogTitle>
				<DialogContent>
					{/* ダイアログの本文 */}
					<DialogContentText>
						<Typography sx={{ fontSize: 18, color: theme.palette.common.black }} noWrap={false}>
							{input.dialogText.split('\n').map((line, key) => (
								<span key={key}>
									{line}
									<br />
								</span>
							))}
						</Typography>
						{input.dialogSupplementaryText && (
							<>
								<Typography sx={{ fontSize: 15, color: theme.palette.common.black, marginTop: 1 }} noWrap={false}>
									{input.dialogSupplementaryText}
								</Typography>
								<Typography sx={{ fontSize: 15, color: theme.palette.common.black }} noWrap={false}>
									{`${loginPageConst.common.label.companyCode}: ${input.companyCode} / ${loginPageConst.common.label.userCode}: ${input.userCode} `}
								</Typography>
							</>
						)}
					</DialogContentText>
					{/* ログイン情報を入力するテキストフィールド */}
					{input.fromList.map((item) => (
						<StyledTextField
							required
							fullWidth
							id={`${item.id}ForDialog`}
							key={item.id}
							label={item.label}
							type={item.type}
							onChange={item.onChange}
							sx={{ marginTop: 3 }}
						/>
					))}
				</DialogContent>
				{/* ダイアログのボタン */}
				<DialogActions>
					<Button id="nextButton" type="button" variant="text" onClick={input.onClickNext} sx={{ marginBottom: 1 }}>
						<Typography sx={{ fontSize: 15, color: theme.palette.primary.main }}>{input.nextButtonName}</Typography>
					</Button>
					{input.onClickBack && (
						<Button id="backButton" type="button" variant="text" onClick={input.onClickBack} sx={{ marginBottom: 1 }}>
							<Typography sx={{ fontSize: 15, color: theme.palette.primary.main }}>
								{loginPageConst.reregistrationStep2Dialog.buttonName.back}
							</Typography>
						</Button>
					)}
					<Button id="cancelButton" type="button" variant="text" onClick={input.onCloseDialog} sx={{ marginBottom: 1 }}>
						<Typography sx={{ fontSize: 15, color: theme.palette.primary.main }}>{loginPageConst.common.buttonName.cancel}</Typography>
					</Button>
				</DialogActions>
				{/* エラー表示 */}
				{input.isError && (
					<Alert id="errorAlert" severity="error" onClose={input.onCloseErrorAlert}>
						<Typography role="lable" aria-label={loginPageConst.common.alert.error} sx={{ fontSize: 13.5, color: theme.palette.common.black }}>
							{loginPageConst.common.alert.error}
						</Typography>
					</Alert>
				)}
				{input.isLoginLockError && (
					<Alert id="loginLockAlert" severity="error" onClose={input.onCloseLoginLockErrorAlert}>
						<Typography
							role="lable"
							aria-label={loginPageConst.common.alert.loginLock}
							sx={{ fontSize: 13.5, color: theme.palette.common.black, whiteSpace: 'pre-wrap' }}
						>
							{loginPageConst.common.alert.loginLock}
						</Typography>
					</Alert>
				)}
				{input.needsChangeInitPassword && (
					<Alert
						id="initPasswordChangeRequestAlert"
						severity="warning"
						action={
							<IconButton color="warning" onClick={input.onClickRedirect} size="small">
								<ArrowForwardIcon />
							</IconButton>
						}
					>
						<Typography
							role="lable"
							aria-label={loginPageConst.common.alert.initPasswordChangeRequest}
							sx={{ fontSize: 13.5, color: theme.palette.common.black }}
						>
							{loginPageConst.common.alert.initPasswordChangeRequest}
						</Typography>
					</Alert>
				)}
				{input.isLocalStorageError && (
					<Alert id="localStorageAlert" severity="error" onClose={input.onCloseLocalStorageErrorAlert}>
						<Typography
							role="lable"
							aria-label={loginPageConst.common.alert.localStorage}
							sx={{ fontSize: 13.5, color: theme.palette.common.black, whiteSpace: 'pre-wrap' }}
						>
							{loginPageConst.common.alert.localStorage}
						</Typography>
					</Alert>
				)}
			</Dialog>
		</ThemeProvider>
	);
};
